import { useEffect, useState } from "react";
import api, { loadError, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, CLEANING } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { downloadBlob } from "@/lib/download";
import { Download, LogOut, LogIn, BedDouble, CalendarClock, Sparkles, AlertTriangle, RefreshCw, Building2, Layers } from "lucide-react";
import { FLOOR_LABELS, BUILDING_LABELS } from "@/components/MonthGrid";

const groupByFloor = (rooms) => {
  const out = [];
  rooms.forEach((r) => {
    const key = `${r.building}-${r.floor}`;
    let g = out[out.length - 1];
    if (!g || g.key !== key) { g = { key, building: r.building, floor: r.floor, rooms: [] }; out.push(g); }
    g.rooms.push(r);
  });
  return out;
};

const TILE = {
  dirty: "bg-red-600 hover:bg-red-700 text-white ring-red-300",
  cleaning: "bg-amber-500 hover:bg-amber-600 text-white ring-amber-300",
  clean: "bg-emerald-600 hover:bg-emerald-700 text-white ring-emerald-300",
};
const NEXT = { dirty: "cleaning", cleaning: "clean", clean: "dirty" };

const Line = ({ icon: Icon, children, testId }) => (
  <div className="flex items-center gap-1.5 text-xs leading-tight" data-testid={testId}><Icon className="w-3.5 h-3.5 shrink-0 opacity-90" /><span className="truncate">{children}</span></div>
);

const Tile = ({ r, onCycle }) => (
  <button
    data-testid={`cleaning-tile-${r.id}`}
    data-cleaning={r.cleaning_status}
    onClick={() => onCycle(r)}
    title={`Clicca per segnare come "${CLEANING[NEXT[r.cleaning_status]].label}"`}
    className={`relative text-left rounded-xl p-4 shadow-sm transition-transform duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-4 ${TILE[r.cleaning_status]} ${r.priority ? "ring-4" : ""}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-heading font-semibold text-lg leading-tight">{r.name}</div>
        <div className="text-xs opacity-80">{r.type}</div>
      </div>
      <span className="text-[10px] uppercase tracking-wider font-bold bg-white/20 rounded-full px-2 py-0.5">{CLEANING[r.cleaning_status].label}</span>
    </div>
    <div className="mt-3 space-y-1">
      {r.departing && <Line icon={LogOut} testId={`tile-departing-${r.id}`}>Check-out oggi: {r.departing.guest_name}</Line>}
      {r.arriving && <Line icon={LogIn} testId={`tile-arriving-${r.id}`}>Arrivo oggi: {r.arriving.guest_name}{r.arriving.arrival_time ? ` · ore ${r.arriving.arrival_time}` : ""}</Line>}
      {r.current && !r.departing && <Line icon={BedDouble}>Occupata: {r.current.guest_name} (fino al {fmtDate(r.current.check_out)})</Line>}
      {r.next && !r.arriving && <Line icon={CalendarClock}>Prossimo arrivo: {fmtDate(r.next.check_in)}</Line>}
      {!r.departing && !r.arriving && !r.current && !r.next && <Line icon={Sparkles}>Libera</Line>}
    </div>
    {r.priority && <div data-testid={`tile-priority-${r.id}`} className="absolute -top-2 -right-2 bg-white text-red-700 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 shadow flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Priorità</div>}
  </button>
);

export default function Cleaning() {
  const [data, setData] = useState(null);
  const load = () => api.get("/cleaning/map").then((r) => setData(r.data)).catch(loadError);
  useEffect(() => { load(); }, []);

  const cycle = async (r) => {
    const next = NEXT[r.cleaning_status];
    try {
      await api.patch(`/rooms/${r.id}/cleaning`, { cleaning_status: next });
      toast.success(`${r.name}: ${CLEANING[next].label}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const c = data?.counts || { dirty: 0, cleaning: 0, clean: 0 };
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Housekeeping</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Mappa Pulizie</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="cleaning-refresh" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5" />Aggiorna</Button>
          <Button data-testid="cleaning-pdf-button" onClick={() => downloadBlob("/cleaning/map.pdf", `mappa_pulizie_${data?.date || "oggi"}.pdf`)} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-10 px-5 font-heading">
            <Download className="w-4 h-4 mr-2" />Scarica PDF
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        <span data-testid="count-dirty" className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-700 px-3 py-1 font-medium"><span className="w-2.5 h-2.5 rounded-full bg-red-600" />Da pulire: {c.dirty}</span>
        <span data-testid="count-cleaning" className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-700 px-3 py-1 font-medium"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" />In pulizia: {c.cleaning}</span>
        <span data-testid="count-clean" className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 font-medium"><span className="w-2.5 h-2.5 rounded-full bg-emerald-600" />Pulite: {c.clean}</span>
        <span className="text-muted-foreground ml-auto self-center text-xs">Percorso ordinato per edificio e piano · clicca una camera per cambiare stato · bordo evidenziato = priorità</span>
      </div>

      {data && data.rooms.length === 0 && <p className="text-muted-foreground">Nessuna camera configurata.</p>}
      {data && groupByFloor(data.rooms).map((g, i) => {
        const todo = g.rooms.filter((r) => r.cleaning_status !== "clean").length;
        return (
          <section key={g.key} data-testid={`cleaning-group-${g.key}`} className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-accent/15 text-accent grid place-items-center text-xs font-bold">{i + 1}</span>
              <h2 className="font-heading text-lg inline-flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" />{BUILDING_LABELS[g.building]}<span className="text-muted-foreground">·</span><Layers className="w-4 h-4 text-muted-foreground" />{FLOOR_LABELS[g.floor]}</h2>
              <span data-testid={`cleaning-group-count-${g.key}`} className={`text-xs rounded-full px-2.5 py-0.5 ${todo ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200" : "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"}`}>{g.rooms.length} camere · {todo ? `${todo} da fare` : "tutte pulite"}</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid={`cleaning-map-${g.key}`}>
              {g.rooms.map((r) => <Tile key={r.id} r={r} onCycle={cycle} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}
